import React from 'react';
import Joyride, { STATUS, ACTIONS } from 'react-joyride-react19-compat';
import { useTutorial } from '../../contexts/TutorialContext';
import { useNavigate } from 'react-router-dom';

const TooltipComponent = ({
  isLastStep,
  primaryProps,
  skip,
  step,
  tooltipProps,
}) => (
  <div {...tooltipProps} className="p-5 bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-sm w-full border dark:border-gray-700">
    {step.title && <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2">{step.title}</h3>}
    <div className="text-gray-600 dark:text-gray-300 text-base">{step.content}</div>
    <div className="flex items-center justify-between mt-6">
      <button {...primaryProps} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-5 rounded-full transition-colors duration-200">
        {isLastStep ? '完了！' : '次へ'}
      </button>
      {step.showSkipButton && <button onClick={skip} className="text-sm text-gray-500 dark:text-gray-400 hover:underline">スキップ</button>}
    </div>
  </div>
);

const TutorialController = () => {
  const { isTutorialActive, tutorialStep, stopTutorial, nextStep } = useTutorial();
  const navigate = useNavigate();

  const steps = [
    {
      target: 'body',
      content: 'Study Questへようこそ！あなたの学習を冒険に変えるお手伝いをします。まずは基本的な使い方を一緒に見ていきましょう。',
      title: 'ようこそ！',
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: '.tutorial-record',
      content: 'すべての冒険は記録から始まります。ここから、日々の学習時間や内容を記録しましょう。',
      title: '1. 学習を記録する',
      placement: 'top',
      action: (element) => element.click(),
      disableBeacon: true,
    },
    {
      target: '#subject-type',
      content: '何の学習を記録しますか？ここで科目を選びます。もしリストになければ、設定ページから自由に追加できますよ。',
      title: '科目を選択',
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '#study-hours',
      content: '次に、学習した時間を入力します。',
      title: '時間を入力',
      placement: 'top',
      disableBeacon: true,
    },
    {
      target: 'button[type="submit"]',
      content: '内容を入力したら、このボタンで記録を保存します。今回は説明だけなので、このまま次のステップに進みましょう。',
      title: '保存して閉じる',
      placement: 'top',
      action: () => {
        const closeButton = document.querySelector('[aria-label="閉じる"]');
        if (closeButton) closeButton.click();
      },
      disableBeacon: true,
    },
    {
      target: '.tutorial-mypage',
      content: '記録した学習はマイページでいつでも確認できます。あなたのレベルや学習グラフを見て、成長を実感しましょう！',
      title: '2. 成長を確認する',
      placement: 'top',
      action: (element) => element.click(),
      disableBeacon: true,
    },
    {
      target: '.tutorial-home',
      content: 'ホーム画面では他の冒険者たちの活動が見られます。互いに刺激し合いましょう！',
      title: '3. 仲間と交流する',
      placement: 'top-start',
      action: (element) => element.click(),
      disableBeacon: true,
    },
    {
      target: 'body',
      content: '基本的な使い方は以上です。さあ、あなただけの学習の冒険を始めましょう！',
      title: '準備完了！',
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: 'body',
      content: 'Study Questには、仲間と競い合うランキング、集中できる自習室、次回の予定管理など、あなたの学習をサポートする機能がたくさんあります。ぜひ色々な機能を試して、学習の冒険を楽しんでください！',
      title: '他にも便利な機能がたくさん！',
      placement: 'center',
      disableBeacon: true,
    },
  ];

  const handleNext = () => {
    const currentStepConfig = steps[tutorialStep];
    
    if (currentStepConfig.action) {
      if (currentStepConfig.target && currentStepConfig.target !== 'body') {
        const element = document.querySelector(currentStepConfig.target);
        if (element) {
          currentStepConfig.action(element);
        }
      } else {
        currentStepConfig.action();
      }
    }

    if (currentStepConfig.path) {
      navigate(currentStepConfig.path);
    }
    
    setTimeout(() => {
      nextStep();
    }, 300);
  };
  
  const handleJoyrideCallback = (data) => {
    const { status, action } = data;
    const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

    if (finishedStatuses.includes(status) || action === ACTIONS.CLOSE || action === ACTIONS.SKIP) {
      stopTutorial();
    }
  };

  const CustomTooltip = (props) => {
    const finalPrimaryProps = {
      ...props.primaryProps,
      onClick: props.isLastStep ? props.primaryProps.onClick : handleNext,
    };
    
    // TooltipComponentに渡すpropsを最終調整
    const finalTooltipProps = {
      ...props,
      primaryProps: finalPrimaryProps,
      skip: stopTutorial, // joyrideのskip関数の代わりに、自前のstopTutorialを渡す
    };

    return <TooltipComponent {...finalTooltipProps} />;
  };

  if (!isTutorialActive) {
    return null;
  }

  return (
    <Joyride
      stepIndex={tutorialStep}
      steps={steps}
      run={isTutorialActive}
      callback={handleJoyrideCallback}
      tooltipComponent={CustomTooltip}
      showProgress
      showSkipButton
      disableOverlayClose={true}
      spotlightClicks={false}
      styles={{
        options: {
          zIndex: 10000,
        },
      }}
      locale={{
        back: '戻る',
        close: '閉じる',
        last: '完了',
        next: '次へ',
        skip: 'スキップ',
      }}
    />
  );
};

export default TutorialController;
